/** @internal */
const isWindowUndefined = () => typeof window === "undefined";

var version="3.3.4";

/** @internal */
function getVisit() {
    // @ts-ignore
    let visit = window?.tracking?.visit();
    if (visit) {
        return visit;
    }
    const cookie = document.cookie
        .split(";")
        .find((c) => c.trim().startsWith("visit"));
    if (cookie) {
        visit = cookie.split("=")[1].trim();
    }
    return visit;
}
/** @internal */
function initLogging() {
    const sendRequest = async (payload) => await fetch(getLoggingUrl(window.location.hostname), {
        method: "POST",
        body: payload,
        headers: {
            "Content-Type": "application/json",
        },
    });
    const logCache = {};
    return {
        log: async (message) => {
            try {
                const messageKey = typeof message === "string" ? message : message.message;
                if (logCache[messageKey]) {
                    return;
                }
                logCache[messageKey] = true;
                let messageObject = {};
                if (typeof message === "string") {
                    messageObject.message = message;
                }
                else {
                    messageObject = { ...message };
                }
                await sendRequest(JSON.stringify([
                    {
                        common: {
                            attributes: {
                                host: window.location.href,
                                agent: window.navigator.userAgent,
                                serviceName: "ab-reader",
                                visit: getVisit(),
                                version,
                            },
                        },
                        logs: [{ ...messageObject, logtype: "browser" }],
                    },
                ]));
            }
            catch (e) {
                console.error("ab-reader failed sending logs", e?.message);
            }
        },
    };
}
function getLoggingUrl(hostname) {
    let scriptSrc = "https://nrlogger-prod.cf-exp.vpsvc.com?partition=exp";
    if (hostname === "localhost" || hostname.startsWith("staging")) {
        scriptSrc = "https://nrlogger-dev.cf-exp.vpsvc.com?partition=exp";
    }
    return scriptSrc;
}

const getABReader = () => window?.abReader || {
    getVariation: () => null,
    getAllExperiments: () => [],
    checkFeature: () => null,
    isAvailable: () => false,
    getTestUserId: () => null,
    fireImpression: () => false,
    fireFeatureImpression: () => false,
};
const logger = initLogging();
let initialize$1 = () => { };
const setInitialize = (initializeMethod) => {
    initialize$1 = initializeMethod;
};
const scriptLoadedEventName = "ab-script-loaded";
const checkForInitializationCalled = (methodName, experimentKey, silent = false) => {
    if (isWindowUndefined()) {
        return false;
    }
    const experimentKeyMessage = experimentKey
        ? ` with experiment key [${experimentKey}]`
        : "";
    if (!window.abReaderInitialized) {
        if (!silent) {
            logInternal({
                message: `Library not initialized before using method ${methodName}${experimentKeyMessage}.`,
            });
        }
        return false;
    }
    return true;
};
const failSafeAndLog = (func, functionName, defaultValue) => {
    try {
        return func();
    }
    catch (error) {
        logInternal({
            message: `ab-reader library failed when calling ${functionName}.`,
            error: JSON.stringify(error),
        });
    }
    return defaultValue;
};
const isAvailable = () => {
    if (!checkForInitializationCalled("isAvailable")) {
        return false;
    }
    return isAvailableInternal();
};
const isAvailableInternal = () => {
    return failSafeAndLog(() => window.abReader?.isAvailable() ?? false, "isAvailable", false);
};
const checkFeature = (featureName) => {
    if (!checkForInitializationCompleted("checkFeature", featureName)) {
        return null;
    }
    return failSafeAndLog(() => getABReader().checkFeature(featureName), "checkFeature", null);
};
const getVariation = (experimentKey) => {
    if (!checkForInitializationCompleted("getVariation", experimentKey)) {
        return null;
    }
    return failSafeAndLog(() => getABReader().getVariation(experimentKey), "getVariation", null);
};
const getAllExperiments = () => {
    if (!checkForInitializationCompleted("getAllExperiments")) {
        return [];
    }
    return failSafeAndLog(getABReader().getAllExperiments, "getAllExperiments", []);
};
const fireImpression = (experimentKey, variationKey, pageParameters) => {
    return fireInternalImpression(() => getABReader().fireImpression(experimentKey, variationKey, pageParameters || null), "fireImpression");
};
const fireFeatureImpression = (feature) => {
    return fireInternalImpression(() => getABReader().fireFeatureImpression(feature.featureName, feature.ruleName, feature.enabled), "fireFeatureImpression");
};
const fireInternalImpression = (coreFireImpression, methodName) => {
    if (isWindowUndefined()) {
        logInternal({
            message: `${methodName} called serverside. `,
            error: "Tracking is not available.",
        });
    }
    const fireImpressionInternal = () => failSafeAndLog(coreFireImpression, methodName, false);
    const fireImpressionCallback = async () => {
        fireImpressionInternal();
        window.removeEventListener(scriptLoadedEventName, fireImpressionCallback);
    };
    if (!checkForInitializationCompleted(methodName, undefined, true)) {
        if (!window.abReaderInitialized) {
            initialize$1();
        }
        window.addEventListener(scriptLoadedEventName, fireImpressionCallback);
        return false;
    }
    return fireImpressionInternal();
};
const getTestUserId = () => {
    if (!checkForInitializationCompleted("getTestUserId")) {
        return null;
    }
    return failSafeAndLog(getABReader().getTestUserId, "getTestUserId", null);
};
const waitTillAvailable = (timeout) => new Promise((resolve) => {
    whenAvailable((available) => resolve(!!available), timeout);
});
const whenAvailable = (callback, timeout) => {
    if (isWindowUndefined()) {
        callback(false);
        return;
    }
    if (isAvailableInternal()) {
        callback(true);
        return;
    }
    let isAvailableResolve;
    const available = new Promise((resolve) => {
        isAvailableResolve = resolve;
    });
    const scriptHandler = () => {
        window.removeEventListener(scriptLoadedEventName, scriptHandler);
        if (isAvailableInternal()) {
            isAvailableResolve(true);
        }
        else {
            initVariationsHandler();
        }
    };
    const initScriptHandler = () => {
        window.addEventListener(scriptLoadedEventName, scriptHandler);
    };
    const variationsResolvedEventName = "variationsResolved";
    const variationsHandler = () => {
        window.removeEventListener(variationsResolvedEventName, variationsHandler);
        isAvailableResolve(true);
    };
    const initVariationsHandler = () => {
        window.addEventListener(variationsResolvedEventName, variationsHandler);
    };
    const promises = [available];
    if (timeout) {
        promises.push(new Promise((resolve) => {
            setTimeout(resolve, timeout);
        }));
    }
    if (!window.abReader) {
        initScriptHandler();
    }
    else {
        initVariationsHandler();
    }
    Promise.race(promises).then(() => {
        callback(isAvailableInternal());
    });
};
const activate = (experimentKey) => {
    const variation = getVariation(experimentKey);
    if (variation) {
        fireImpression(experimentKey, variation);
    }
    return variation;
};
const isFeatureEnabled = (featureName) => {
    const feature = checkFeature(featureName);
    if (feature !== null) {
        fireFeatureImpression(feature);
        if (feature.enabled === "true") {
            return true;
        }
    }
    return false;
};
const checkForInitializationCompleted = (methodName, experimentKey, silent = false) => {
    if (!checkForInitializationCalled(methodName, experimentKey, silent)) {
        return false;
    }
    const experimentKeyMessage = experimentKey
        ? ` with experiment key [${experimentKey}]`
        : "";
    if (window.abReader === undefined) {
        if (!silent) {
            logInternal({
                message: `ab-reader library not ready before using method ${methodName}${experimentKeyMessage}.`,
            });
        }
        return false;
    }
    return true;
};
function logInternal(message) {
    logger.log(message);
}

const initialize = () => {
    if (isWindowUndefined()) {
        return;
    }
    //if script has been injected
    if (window.abReaderInitialized)
        return;
    //Inject from CDN (npm)
    const script = document.createElement("script");
    script.type = "application/javascript";
    script.async = true;
    script.src = getScriptSourceUrl(window.location.hostname);
    script.onload = () => window.dispatchEvent(new CustomEvent(scriptLoadedEventName));
    // Insert our script next to the first script element.
    document.head.appendChild(script);
    window.abReaderInitialized = true;
};
setInitialize(initialize);
function getScriptSourceUrl(hostname) {
    let scriptSrc = `https://wexp.exp.vpsvc.com/experimentation/ab-lib/ab-reader.js`;
    try {
        let result = hostname.match(/vistaprint\.[a-z]{2,3}(\.[a-z]{2,3})?/);
        if (result && result[0]) {
            scriptSrc = `https://www.${result[0]}/experimentation/ab-lib/ab-reader.js`;
        }
        if (hostname === "localhost" || hostname.startsWith("staging.vistaprint")) {
            scriptSrc =
                "https://staging.vistaprint.com/experimentation/ab-lib/ab-reader.js";
        }
        return scriptSrc;
    }
    catch (error) {
        logInternal({
            message: `There was an error trying to get the script source for the following hostname: '${hostname}'`,
        });
        return scriptSrc;
    }
}

export { activate, fireImpression, getAllExperiments, getTestUserId, getVariation, initialize, isAvailable, isFeatureEnabled, waitTillAvailable, whenAvailable };
//# sourceMappingURL=main_npm.js.map
